import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { useEffect } from "react";

type Props = {
  initialHtml: string;
  onChange: (html: string) => void;
};

export function RichMailEditor({ initialHtml, onChange }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: false }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: "Scrivi il messaggio…" }),
      Image.configure({ inline: true, allowBase64: true }),
    ],
    content: initialHtml || "<p></p>",
    editorProps: {
      attributes: {
        class: "ms-editor-prose",
        spellcheck: "true",
      },
      handlePaste: (_view, event) => {
        const items = event.clipboardData?.items;
        if (!items || !editor) return false;
        for (const item of items) {
          if (item.type.startsWith("image/")) {
            const file = item.getAsFile();
            if (!file) continue;
            const reader = new FileReader();
            reader.onload = () => {
              const src = String(reader.result || "");
              if (src) editor.chain().focus().setImage({ src }).run();
            };
            reader.readAsDataURL(file);
            event.preventDefault();
            return true;
          }
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (initialHtml && current === "<p></p>") {
      editor.commands.setContent(initialHtml, { emitUpdate: false });
    }
  }, [editor, initialHtml]);

  if (!editor) return <div className="ms-editor-shell loading">Caricamento editor…</div>;

  return (
    <div className="ms-editor-shell">
      <div className="ms-editor-toolbar" role="toolbar" aria-label="Formattazione">
        <div className="ms-tb-group">
          <TbBtn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Grassetto">
            <IconBold />
          </TbBtn>
          <TbBtn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Corsivo">
            <IconItalic />
          </TbBtn>
          <TbBtn active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Sottolineato">
            <IconUnderline />
          </TbBtn>
          <TbBtn active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} title="Barrato">
            <IconStrike />
          </TbBtn>
          <TbBtn active={editor.isActive("highlight")} onClick={() => editor.chain().focus().toggleHighlight().run()} title="Evidenzia">
            <IconHighlight />
          </TbBtn>
        </div>
        <div className="ms-tb-sep" />
        <div className="ms-tb-group">
          <TbBtn active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Titolo 1">
            H1
          </TbBtn>
          <TbBtn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Titolo 2">
            H2
          </TbBtn>
          <TbBtn active={editor.isActive("paragraph")} onClick={() => editor.chain().focus().setParagraph().run()} title="Paragrafo">
            ¶
          </TbBtn>
        </div>
        <div className="ms-tb-sep" />
        <div className="ms-tb-group">
          <TbBtn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Elenco">
            <IconList />
          </TbBtn>
          <TbBtn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Elenco numerato">
            <IconOrdered />
          </TbBtn>
          <TbBtn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citazione">
            <IconQuote />
          </TbBtn>
          <TbBtn active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Blocco codice">
            {"</>"}
          </TbBtn>
        </div>
        <div className="ms-tb-sep" />
        <div className="ms-tb-group">
          <TbBtn active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()} title="Allinea a sinistra">
            <IconAlignLeft />
          </TbBtn>
          <TbBtn active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()} title="Centra">
            <IconAlignCenter />
          </TbBtn>
          <TbBtn active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()} title="Allinea a destra">
            <IconAlignRight />
          </TbBtn>
        </div>
        <div className="ms-tb-sep" />
        <div className="ms-tb-group">
          <TbBtn
            active={editor.isActive("link")}
            onClick={() => {
              const prev = editor.getAttributes("link").href as string | undefined;
              const url = window.prompt("URL del link", prev || "https://");
              if (url === null) return;
              if (url === "") {
                editor.chain().focus().extendMarkRange("link").unsetLink().run();
                return;
              }
              editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
            }}
            title="Inserisci link"
          >
            <IconLink />
          </TbBtn>
          <TbBtn
            onClick={() => {
              const url = window.prompt("URL immagine");
              if (url) editor.chain().focus().setImage({ src: url }).run();
            }}
            title="Inserisci immagine"
          >
            <IconImage />
          </TbBtn>
          <TbBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Linea orizzontale">
            ―
          </TbBtn>
        </div>
        <div className="ms-tb-sep" />
        <div className="ms-tb-group">
          <label className="ms-tb-color" title="Colore testo">
            <input
              type="color"
              value={editor.getAttributes("textStyle").color || "#efe7d8"}
              onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            />
          </label>
          <TbBtn onClick={() => editor.chain().focus().unsetColor().run()} title="Reset colore">
            A
          </TbBtn>
          <TbBtn onClick={() => editor.chain().focus().undo().run()} title="Annulla">
            ↶
          </TbBtn>
          <TbBtn onClick={() => editor.chain().focus().redo().run()} title="Ripeti">
            ↷
          </TbBtn>
        </div>
      </div>
      <EditorContent editor={editor} className="ms-editor-surface" />
    </div>
  );
}

function TbBtn({
  children,
  onClick,
  active,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  title: string;
}) {
  return (
    <button type="button" className={`ms-tb-btn ${active ? "active" : ""}`} onClick={onClick} title={title} aria-label={title}>
      {children}
    </button>
  );
}

function IconBold() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
      <path d="M7 5h6a3.5 3.5 0 0 1 0 7H7zM7 12h7a3.5 3.5 0 0 1 0 7H7z" />
    </svg>
  );
}
function IconItalic() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M10 5h9M5 19h9M14 5l-5 14" />
    </svg>
  );
}
function IconUnderline() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M7 5v6a5 5 0 0 0 10 0V5M5 19h14" />
    </svg>
  );
}
function IconStrike() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M5 12h14M8 7c1.5-2 8-2 8 1.5 0 2-2 2.5-4 3M8 17c2 1.5 8 1 8-1.5" />
    </svg>
  );
}
function IconHighlight() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3zM4 20h16" />
    </svg>
  );
}
function IconList() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="5" cy="7" r="1.5" />
      <circle cx="5" cy="12" r="1.5" />
      <circle cx="5" cy="17" r="1.5" />
      <rect x="9" y="6" width="11" height="2" rx="1" />
      <rect x="9" y="11" width="11" height="2" rx="1" />
      <rect x="9" y="16" width="11" height="2" rx="1" />
    </svg>
  );
}
function IconOrdered() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <text x="2" y="9" fontSize="7" fontFamily="sans-serif">
        1
      </text>
      <text x="2" y="14" fontSize="7" fontFamily="sans-serif">
        2
      </text>
      <text x="2" y="19" fontSize="7" fontFamily="sans-serif">
        3
      </text>
      <rect x="9" y="6" width="11" height="2" rx="1" />
      <rect x="9" y="11" width="11" height="2" rx="1" />
      <rect x="9" y="16" width="11" height="2" rx="1" />
    </svg>
  );
}
function IconQuote() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M7 10c0-2 1.5-4 4-4v2c-1.2 0-2 .8-2 2h2v6H5v-6h2zm8 0c0-2 1.5-4 4-4v2c-1.2 0-2 .8-2 2h2v6h-6v-6h2z" />
    </svg>
  );
}
function IconLink() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" />
      <path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" />
    </svg>
  );
}
function IconImage() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="10" r="1.5" />
      <path d="M21 16l-5-5-8 8" />
    </svg>
  );
}
function IconAlignLeft() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <rect x="4" y="6" width="16" height="2" rx="1" />
      <rect x="4" y="11" width="12" height="2" rx="1" />
      <rect x="4" y="16" width="14" height="2" rx="1" />
    </svg>
  );
}
function IconAlignCenter() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <rect x="4" y="6" width="16" height="2" rx="1" />
      <rect x="6" y="11" width="12" height="2" rx="1" />
      <rect x="5" y="16" width="14" height="2" rx="1" />
    </svg>
  );
}
function IconAlignRight() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <rect x="4" y="6" width="16" height="2" rx="1" />
      <rect x="8" y="11" width="12" height="2" rx="1" />
      <rect x="6" y="16" width="14" height="2" rx="1" />
    </svg>
  );
}
